import { useReducer, useCallback, useEffect, useRef } from "react"
import dayjs from "dayjs"
import { calculateMinWidth } from "src/utils/table-render-functions"
import { DATE_FORMAT } from "src/utils/constants"
import { formatCpfCnpj } from "src/utils/format-cpf-cnpj"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

export const useRatingEmissaoTable = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    
    // Garante que os dados do cetip anterior não preencham a tabela ao mudar o cetip
    const loadedRef = useRef(cetipIsin)
    
    useEffect(() => {
        if (loadedRef.current === cetipIsin) return

        loadedRef.current = cetipIsin
    
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: []})
        dispatch({ type: "CLOSE_ALERT" })
            
    }, [cetipIsin])

    const buildTable = useCallback(
        (dadosCabecalho) => {
            const {
                cetip,
                isin,
                dataVencimento,
                dataResgateAntecipado,
                cpfCnpj,
                devedor,
                setor,
                subsetor
            } = dadosCabecalho

            dispatch({
                type: "SET_TABLE",
                payload: [
                    {
                        cells: [
                            `CETIP: ${cetip || ""}`,
                            `ISIN: ${isin || ""}`,
                            `Vencimento: ${
                                dataVencimento
                                    ? dayjs(dataVencimento).format("DD/MM/YYYY")
                                    : ""
                            }`,
                            `Data Resgate Antecipado: ${
                                dataResgateAntecipado 
                                    ? dayjs(dataResgateAntecipado).format("DD/MM/YYYY") 
                                    : ""
                            }`,
                        ],
                    },
                    {
                        cells: [
                            `CPF/CNPJ: ${cpfCnpj ? formatCpfCnpj(cpfCnpj) : ""}`,
                            `Devedor: ${devedor || ""}`,
                            `Setor: ${setor || ""}`,
                            `Subsetor: ${subsetor || ""}`,
                        ],
                    },
                ],
            })
        },
        []
    )

    const loadTableRows = useCallback(() => {
        dispatch({ type: "SET_LOADING", payload: true })
        emissaoService
            .ratingEmissao("get", `?CETIP=${cetipIsin?.toUpperCase()}`)
            .then(({ body }) => {
                // CORREÇÃO 1: Impede a quebra se o .results não existir
                const listaResultados = body?.results || (Array.isArray(body) ? body : [])

                const rows = ordenaPorDataReferencia(listaResultados, "desc").map(
                    (obj, i) => ({ ...obj, id: i })
                )

                // Extrai os dados da primeira linha retornada pela API
                const primeiraLinha = rows[0] || {}
                console.log('primeira_linha:', primeiraLinha)

                // Monta a tabela priorizando os dados da API, com fallback para dadosEmpresa
                buildTable({
                    cetip: primeiraLinha.CETIP || dadosEmpresa?.CETIP || cetipIsin,
                    isin: primeiraLinha.ISIN || dadosEmpresa?.ISIN,
                    // CORREÇÃO 2: Chave correta do vencimento
                    dataVencimento: primeiraLinha.DataVencimento || dadosEmpresa?.DataVencimento,
                    dataResgateAntecipado: primeiraLinha.DataResgateAntecipado || dadosEmpresa?.DataResgateAntecipado,
                    cpfCnpj: primeiraLinha.CPFCNPJ || primeiraLinha.CPF_CNPJ_Devedor || dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                    devedor: primeiraLinha.Devedor || dadosEmpresa?.Devedor,
                    setor: primeiraLinha.Setor,
                    // CORREÇÃO 3: Chave correta do subsetor
                    subsetor: primeiraLinha.Subsetor || primeiraLinha.SubSetor
                })

                dispatch({ type: "SET_ROWS", payload: rows })
            })
            .catch(() => {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
                
                // Em caso de erro/vazio, ainda tentamos montar o cabeçalho com o que temos
                buildTable({
                    cetip: cetipIsin || dadosEmpresa?.CETIP,
                    isin: dadosEmpresa?.ISIN,
                    dataVencimento: dadosEmpresa?.DataVencimento,
                    dataResgateAntecipado: dadosEmpresa?.DataResgateAntecipado,
                    cpfCnpj: dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                    devedor: dadosEmpresa?.Devedor,
                    setor: dadosEmpresa?.Setor,
                    subsetor: dadosEmpresa?.Subsetor
                })
            })
            .finally(() => dispatch({ type: "SET_LOADING", payload: false }))
    }, [cetipIsin, emissaoService, buildTable, dadosEmpresa])

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]

        if (state.rows.length === 0) {
            lastItem = {
                CETIP: cetipIsin,
                CPFCNPJ: dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                DataReferencia: dayjs().format(DATE_FORMAT),
                Devedor: dadosEmpresa?.Devedor || "N/A",
                Setor: "N/A",
                SubSetor: "N/A",
                Fonte: "N/A",
                Rating: "N/A",
            }
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            await emissaoService.ratingEmissao("post", lastItem)
            loadTableRows()
        } catch (error) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipIsin, dadosEmpresa, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            const { ID } = row

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingEmissao("delete", ID)
                loadTableRows()
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const { ID } = row

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingEmissao("patch", ID, row)

                const updatedRows = state.rows.map((existingRow) =>
                    existingRow.ID === ID
                        ? { ...existingRow, ...row }
                        : existingRow
                )
                dispatch({ type: "SET_ROWS", payload: updatedRows })

                return row
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })
                throw error
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, state.rows]
    )

    return {
        state: {
            ...state,
            minWidthAnaliseCredito: calculateMinWidth(
                state.rows,
                "AnaliseCreditoEmissao"
            ),
            minWidthObservacao: calculateMinWidth(state.rows, "Observacao"),
        },
        actions: {
            loadTableRows,
            loadTableRowsDevedor: loadTableRows,
            setAlert: (payload) => dispatch({ type: "SET_ALERT", payload }),
            closeAlert: () => dispatch({ type: "CLOSE_ALERT" }),
            addRow,
            deleteRow,
            updateRow,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
        },
    }
}